import React, { useState } from 'react';
import { Search, Filter, Grid, List, Star, Eye, Download } from 'lucide-react';
import { motion } from 'framer-motion';
import ResumeTemplatePreview from './ResumeTemplatePreview';

const TemplateLibrary = ({ onSelectTemplate }) => {
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const handleDownloadTemplate = () => {
    alert('To download your resume, please select a template and use the editor. The download feature is available in the resume editor.');
  };

  // 20 Resume Templates
  const templates = [
    // Modern Templates
    {
      id: 'modern-1',
      name: 'Modern Professional',
      category: 'modern',
      description: 'Clean lines with bold headers and modern typography',
      features: ['ATS-friendly', 'Modern design', 'Easy to customize'],
      popularity: 95,
      difficulty: 'Easy',
      color: 'blue'
    },
    {
      id: 'modern-2',
      name: 'Modern Minimal',
      category: 'modern',
      description: 'Minimalist approach with clean lines and plenty of white space',
      features: ['Clean layout', 'Tech-focused', 'ATS-optimized'],
      popularity: 88,
      difficulty: 'Easy',
      color: 'gray'
    },
    {
      id: 'creative-1',
      name: 'Creative Portfolio',
      category: 'creative',
      description: 'Colorful and creative design for creative professionals',
      features: ['Eye-catching design', 'Portfolio integration', 'Creative layout'],
      popularity: 87,
      difficulty: 'Medium',
      color: 'purple'
    },
    {
      id: 'professional-2',
      name: 'Executive Summary',
      category: 'professional',
      description: 'Sophisticated design for senior professionals',
      features: ['Executive presence', 'Traditional layout', 'Authority'],
      popularity: 89,
      difficulty: 'Easy',
      color: 'dark-blue'
    },

    // Professional Templates
    {
      id: 'professional-1',
      name: 'Classic Professional',
      category: 'professional',
      description: 'Traditional two-column layout perfect for corporate environments',
      features: ['Conservative design', 'ATS-optimized', 'Professional look'],
      popularity: 92,
      difficulty: 'Easy',
      color: 'navy'
    },
    {
      id: 'two-column-1',
      name: 'Two Column Classic',
      category: 'professional',
      description: 'Classic two-column layout with sidebar',
      features: ['Two column layout', 'Sidebar design', 'Professional look'],
      popularity: 88,
      difficulty: 'Easy',
      color: 'gray'
    },
    {
      id: 'classic-1',
      name: 'Traditional Classic',
      category: 'classic',
      description: 'Traditional serif font design for formal positions',
      features: ['Serif typography', 'Traditional layout', 'Formal presentation'],
      popularity: 82,
      difficulty: 'Easy',
      color: 'black'
    },
    {
      id: 'tech-1',
      name: 'Developer Focus',
      category: 'tech',
      description: 'Perfect for software developers and tech professionals',
      features: ['Tech focused', 'Code styling', 'Developer friendly'],
      popularity: 89,
      difficulty: 'Easy',
      color: 'green'
    },

    // Creative Templates
    {
      id: 'creative-2',
      name: 'Artistic Flair',
      category: 'creative',
      description: 'Unique artistic design for creative industries',
      features: ['Visual impact', 'Design focused', 'Portfolio ready'],
      popularity: 83,
      difficulty: 'Hard',
      color: 'pink'
    },
    {
      id: 'executive-1',
      name: 'C-Suite Executive',
      category: 'executive',
      description: 'Sophisticated design for executive and leadership positions',
      features: ['Executive presence', 'Leadership focus', 'Premium design'],
      popularity: 91,
      difficulty: 'Easy',
      color: 'navy'
    },
    {
      id: 'executive-2',
      name: 'Board Ready',
      category: 'executive',
      description: 'Ultra-premium design for C-level executives',
      features: ['Ultra-premium', 'CEO level', 'Maximum impact'],
      popularity: 88,
      difficulty: 'Medium',
      color: 'black'
    },
    {
      id: 'minimalist-1',
      name: 'Ultra Minimal',
      category: 'minimalist',
      description: 'Extremely clean and simple design',
      features: ['Clean design', 'Easy to read', 'Content-focused'],
      popularity: 90,
      difficulty: 'Easy',
      color: 'gray'
    },

    // Minimalist Templates
    {
      id: 'minimalist-2',
      name: 'Simple Elegance',
      category: 'minimalist',
      description: 'Elegant simplicity for any profession',
      features: ['Elegant', 'Simple', 'Universal appeal'],
      popularity: 86,
      difficulty: 'Easy',
      color: 'white'
    },
    {
      id: 'academic-1',
      name: 'Academic Scholar',
      category: 'academic',
      description: 'Perfect for academic and research positions',
      features: ['Academic focus', 'Research oriented', 'Scholarly'],
      popularity: 77,
      difficulty: 'Medium',
      color: 'maroon'
    },
    {
      id: 'tech-2',
      name: 'Startup Ready',
      category: 'tech',
      description: 'Modern design perfect for startup environments',
      features: ['Startup vibe', 'Modern design', 'Tech friendly'],
      popularity: 88,
      difficulty: 'Easy',
      color: 'green'
    },
    {
      id: 'healthcare-1',
      name: 'Medical Professional',
      category: 'healthcare',
      description: 'Clean design for healthcare professionals',
      features: ['Healthcare focus', 'Clean design', 'Professional look'],
      popularity: 82,
      difficulty: 'Easy',
      color: 'green'
    },

    // Specialized Templates
    {
      id: 'finance-1',
      name: 'Financial Analyst',
      category: 'finance',
      description: 'Conservative design for finance professionals',
      features: ['Conservative design', 'Finance focus', 'Professional look'],
      popularity: 85,
      difficulty: 'Easy',
      color: 'blue'
    },
    {
      id: 'sales-1',
      name: 'Sales Professional',
      category: 'sales',
      description: 'Dynamic design for sales professionals',
      features: ['Dynamic design', 'Sales focus', 'Results oriented'],
      popularity: 88,
      difficulty: 'Easy',
      color: 'orange'
    },
    {
      id: 'academic-2',
      name: 'Research Professional',
      category: 'academic',
      description: 'Comprehensive design for research professionals',
      features: ['Research focus', 'Comprehensive layout', 'Academic format'],
      popularity: 65,
      difficulty: 'Hard',
      color: 'maroon'
    },
    {
      id: 'healthcare-2',
      name: 'Clinical Excellence',
      category: 'healthcare',
      description: 'Professional design for clinical positions',
      features: ['Clinical focus', 'Professional styling', 'Medical format'],
      popularity: 78,
      difficulty: 'Medium',
      color: 'green'
    }
  ];

  const categories = [
    { value: 'all', label: 'All Templates', count: templates.length },
    { value: 'modern', label: 'Modern', count: templates.filter(t => t.category === 'modern').length },
    { value: 'professional', label: 'Professional', count: templates.filter(t => t.category === 'professional').length },
    { value: 'creative', label: 'Creative', count: templates.filter(t => t.category === 'creative').length },
    { value: 'executive', label: 'Executive', count: templates.filter(t => t.category === 'executive').length },
    { value: 'minimalist', label: 'Minimalist', count: templates.filter(t => t.category === 'minimalist').length },
    { value: 'academic', label: 'Academic', count: templates.filter(t => t.category === 'academic').length },
    { value: 'tech', label: 'Tech', count: templates.filter(t => t.category === 'tech').length },
    { value: 'classic', label: 'Classic', count: templates.filter(t => t.category === 'classic').length },
    { value: 'healthcare', label: 'Healthcare', count: templates.filter(t => t.category === 'healthcare').length },
    { value: 'finance', label: 'Finance', count: templates.filter(t => t.category === 'finance').length },
    { value: 'sales', label: 'Sales', count: templates.filter(t => t.category === 'sales').length }
  ];

  // Filter and sort templates
  const filteredTemplates = templates
    .filter(template => {
      const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           template.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'popular':
          return b.popularity - a.popularity;
        case 'name':
          return a.name.localeCompare(b.name);
        case 'difficulty':
          const difficultyOrder = { 'Easy': 1, 'Medium': 2, 'Hard': 3 };
          return difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty];
        default:
          return 0;
      }
    });

  const TemplateCard = ({ template, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
    >
      {/* Template Preview */}
      <div className="h-96 bg-gray-50 relative group overflow-hidden">
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <div className={`w-16 h-20 mx-auto mb-4 rounded border-2 ${
              template.color === 'blue' ? 'bg-blue-100 border-blue-300' :
              template.color === 'gray' ? 'bg-gray-100 border-gray-300' :
              template.color === 'purple' ? 'bg-purple-100 border-purple-300' :
              template.color === 'navy' ? 'bg-blue-900 border-blue-800' :
              template.color === 'dark-blue' ? 'bg-blue-800 border-blue-700' :
              template.color === 'green' ? 'bg-green-100 border-green-300' :
              template.color === 'orange' ? 'bg-orange-100 border-orange-300' :
              template.color === 'pink' ? 'bg-pink-100 border-pink-300' :
              template.color === 'black' ? 'bg-gray-800 border-gray-700' :
              template.color === 'white' ? 'bg-white border-gray-300' :
              template.color === 'maroon' ? 'bg-red-900 border-red-800' :
              'bg-gray-100 border-gray-300'
            }`}>
              <div className="w-full h-full flex items-center justify-center">
                <span className="text-xs font-bold text-gray-600">
                  {template.name.charAt(0)}
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-2">{template.name}</p>
            <div className="flex justify-center space-x-1">
              <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
              <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
              <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
            </div>
          </div>
        </div>
        
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <div className="flex space-x-2">
            <button 
              onClick={() => setPreviewTemplate(template)}
              className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button 
              onClick={handleDownloadTemplate}
              className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100"
              title="Download resume (available in editor)"
            >
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Template Info */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-gray-900 text-sm">
            {template.name}
          </h3>
          <div className="flex items-center text-xs text-gray-500">
            <Star className="w-3 h-3 text-yellow-500 mr-1" />
            {template.popularity}%
          </div>
        </div>
        
        <p className="text-xs text-gray-600 mb-3 line-clamp-2">
          {template.description}
        </p>

        <div className="flex items-center justify-between mb-3">
          <span className={`text-xs px-2 py-1 rounded-full ${
            template.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
            template.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
            'bg-red-100 text-red-700'
          }`}>
            {template.difficulty}
          </span>
          <span className="text-xs text-gray-500 capitalize">
            {template.category}
          </span>
        </div>

        <button
          onClick={() => onSelectTemplate(template)}
          className="w-full bg-primary-600 text-white py-2 px-3 rounded text-sm hover:bg-primary-700 transition-colors duration-200"
        >
          Use Template
        </button>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Choose Your Perfect Resume Template
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Browse our collection of 20 professionally designed resume templates. Each template is ATS-friendly and optimized for different industries and experience levels.
          </p>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="lg:w-64">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label} ({category.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div className="lg:w-48">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="popular">Most Popular</option>
                <option value="name">Name A-Z</option>
                <option value="difficulty">Difficulty</option>
              </select>
            </div>

            {/* View Mode */}
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600'}`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600'}`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            Showing {filteredTemplates.length} of {templates.length} templates
          </p>
        </div>

        {/* Template Grid */}
        <div className={`grid gap-6 ${
          viewMode === 'grid' 
            ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
            : 'grid-cols-1'
        }`}>
          {filteredTemplates.map((template, index) => (
            <TemplateCard key={template.id} template={template} index={index} />
          ))}
        </div>

        {/* No Results */}
        {filteredTemplates.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Search className="w-12 h-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
          </div>
        )}

        {/* Template Preview Modal */}
        {previewTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">
                    {previewTemplate.name} - Preview
                  </h3>
                  <button
                    onClick={() => setPreviewTemplate(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="border rounded-lg overflow-hidden bg-gray-50 p-8">
                  <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
                    <div className="text-center mb-6">
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">Template Preview</h2>
                      <p className="text-gray-600">{previewTemplate.description}</p>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Template Name:</span>
                        <span className="text-gray-600">{previewTemplate.name}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Category:</span>
                        <span className="text-gray-600 capitalize">{previewTemplate.category}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Difficulty:</span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          previewTemplate.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                          previewTemplate.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {previewTemplate.difficulty}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Popularity:</span>
                        <span className="text-gray-600">{previewTemplate.popularity}%</span>
                      </div>
                    </div>
                    
                    <div className="mt-6">
                      <h3 className="font-medium text-gray-900 mb-3">Features:</h3>
                      <div className="flex flex-wrap gap-2">
                        {previewTemplate.features.map((feature, index) => (
                          <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setPreviewTemplate(null)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      onSelectTemplate(previewTemplate);
                      setPreviewTemplate(null);
                    }}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Use This Template
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateLibrary;
